import type { FormEvent } from 'react'
import type {
  BuyerRegistrationFormData,
  BuyerRegistrationInputName,
  BuyerRegistrationPublicFormData,
  BuyerRegistrationPublicPayload
} from 'types/buyer'
import type {
  SellerRegistrationFormData,
  SellerRegistrationInputName,
  SellerRegistrationPublicFormData,
  SellerRegistrationPublicPayload
} from 'types/seller'
import type { ClientType, Consents, RegistrationFormElement, User } from 'types'

import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import InputMask from 'react-input-mask'

import { useStore } from 'store'
import { addParamToPath, getInput, sc, setValue, trimFormData } from 'utils'
import { registrationFormAutoFill } from 'utils/devTools'
import { validateRegistrationForm } from 'utils/pageValidators'
import useIsPublicPage from 'hooks/useIsPublicPage'
import { Sun2Icon, Water2Icon, Wind2Icon } from 'icons'
import copy from 'copy.json'
import { adminPanelPaths, paths } from 'paths'

import Button from 'components/Button'
import Checkbox, { CheckboxLabel } from 'components/Checkbox'
import Input, { PrefixedInput } from 'components/Input'
import InputLabel from 'components/InputLabel'
import Radio, { ActiveRadioLabel, CombinedRadios } from 'components/Radio'
import Select from 'components/Select'
import Spinner from 'components/Spinner'

type LocationState = {
  userType: User['type']
}

export default function RegistrationScreen() {
  const isPublicPage = useIsPublicPage()
  const { api, registerUser, registerUserPublic, loading, fieldErrors } = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const userTypeQuery = new URLSearchParams(location.search).get('usertype') as User['type']
  const initialUserType = (location.state as LocationState)?.userType || userTypeQuery || 'seller'
  const [userType, setUserType] = useState<ClientType>(initialUserType)
  const [isNavigatingOut, setIsNavigatingOut] = useState(true)
  const [consents, setConsents] = useState<Consents>([])
  const [errors, setErrors] = useState<{
    [key in BuyerRegistrationInputName | SellerRegistrationInputName]?: string
  }>({})

  const termsAndConditionsUrl = consents.find(
    consent => consent.name === 'TERMS_AND_CONDITIONS'
  )?.file_url
  const privacyUrl = consents.find(consent => consent.name === 'PRIVACY')?.file_url

  const getCompany = async (taxIdentifier: string) => {
    const company = await api.getCompany(taxIdentifier)

    if (!company) return

    setValue('company_name', company.name)
    setErrors(state => ({ ...state, company_name: undefined }))
  }

  const validateInput = (event: FormEvent<HTMLInputElement>) => {
    const currentTarget = event.currentTarget
    const validation = validateRegistrationForm(currentTarget)

    setErrors(state => ({ ...state, [currentTarget.name]: validation }))

    return validation
  }

  const registration = async (form: EventTarget & RegistrationFormElement) => {
    if (loading.registerUser) return

    const inputs = Array.from(form.elements) as HTMLInputElement[]
    const errors = inputs.reduce(
      (result, input) => ({ ...result, [input.name]: validateRegistrationForm(input) }),
      {}
    )

    const hasErrors = Object.values(errors).some(error => error !== undefined)

    if (hasErrors) return setErrors(errors)

    const data = await registerUser(
      form.elements.contact_email.value,
      userType,
      Array.from(trimFormData(new FormData(form))).reduce(
        (result, [name, value]) => ({ ...result, [name]: value }),
        {} as BuyerRegistrationFormData | SellerRegistrationFormData
      )
    )

    if (!data) return

    setIsNavigatingOut(true)

    setTimeout(() => {
      if (userType === 'seller') navigate(addParamToPath(adminPanelPaths.seller.search, data.id))
      if (userType === 'buyer') navigate(addParamToPath(adminPanelPaths.buyer.search, data.id))
    }, 500)
  }

  const publicRegistration = async (form: EventTarget & RegistrationFormElement) => {
    if (loading.registerUserPublic) return

    const inputs = Array.from(form.elements) as HTMLInputElement[]
    const errors = inputs.reduce(
      (result, input) => ({ ...result, [input.name]: validateRegistrationForm(input) }),
      {}
    )

    const hasErrors = Object.values(errors).some(error => error !== undefined)

    if (hasErrors) return setErrors(errors)

    const values = Array.from(trimFormData(new FormData(form))).reduce(
      (result, [name, value]) => ({ ...result, [name]: value }),
      {} as BuyerRegistrationPublicFormData | SellerRegistrationPublicFormData
    )

    const payload: BuyerRegistrationPublicPayload | SellerRegistrationPublicPayload = {
      ...values,
      marketing_consent: {
        agreed: values.marketing_consent === 'on'
      },
      marketing_third_party_consent: {
        agreed: values.marketing_third_party_consent === 'on'
      }
    }

    const success = await registerUserPublic(userType, payload)

    if (!success) return

    setIsNavigatingOut(true)

    setTimeout(
      () => navigate(`/${paths.registration_success}`, { state: { email: values.contact_email } }),
      500
    )
  }

  const submit = async (event: FormEvent<RegistrationFormElement>) => {
    event.preventDefault()

    isPublicPage ? publicRegistration(event.currentTarget) : registration(event.currentTarget)
  }

  useEffect(() => {
    if (!isPublicPage) return

    const getConsents = async (clientType: ClientType) => {
      setConsents((await api.getConsents(clientType)) || [])
    }

    getConsents(userType)
  }, [api, isPublicPage, userType])

  useEffect(() => setIsNavigatingOut(false), [])

  useEffect(() => {
    setErrors(fieldErrors)
    getInput(Object.keys(fieldErrors)[0])?.scrollIntoView({ behavior: 'smooth' })
  }, [fieldErrors])

  return (
    <Wrapper>
      <Info className="dark" hidden={isNavigatingOut}>
        <h1>
          {copy.page.registration.heading}
          <strong>{copy.page.registration.heading_bold}</strong>
        </h1>

        <p>{copy.page.registration.paragraph}</p>

        <div>
          <Sun2Icon />
          <Water2Icon />
          <Wind2Icon />
        </div>
      </Info>

      <Form onSubmit={submit} noValidate hidden={isNavigatingOut}>
        {process.env.NODE_ENV === 'development' && (
          <button type="button" onClick={registrationFormAutoFill}>
            Auto fill
          </button>
        )}

        <h1>{copy.page.registration.form.heading}</h1>
        <p>{copy.page.registration.form.paragraph}</p>

        <CombinedRadios>
          <span>{copy.page.registration.form.radio_label}</span>
          <div>
            <ActiveRadioLabel active={userType === 'buyer'}>
              <Radio
                value="buyer"
                checked={userType === 'buyer'}
                onChange={() => setUserType('buyer')}
              />
              {copy.page.registration.form.radio_buyer}
            </ActiveRadioLabel>

            <ActiveRadioLabel active={userType === 'seller'}>
              <Radio
                value="seller"
                checked={userType === 'seller'}
                onChange={() => setUserType('seller')}
              />
              {copy.page.registration.form.radio_seller}
            </ActiveRadioLabel>
          </div>
        </CombinedRadios>

        <InputLabel>
          <span>{copy.common.form.tax_identifier}</span>
          <span>{errors.tax_identifier}</span>
          <Input
            name="tax_identifier"
            onInput={event => !validateInput(event) && getCompany(event.currentTarget.value)}
            onBlur={validateInput}
          />
        </InputLabel>

        <InputLabel>
          <span>{copy.common.form.company_name}</span>
          <span>{errors.company_name}</span>
          <Input name="company_name" onBlur={validateInput} />
        </InputLabel>

        {userType === 'seller' && (
          <InputLabel>
            <span>{copy.common.form.concession_identifier}</span>
            <span>{errors.concession_identifier}</span>
            <Input name="concession_identifier" onBlur={validateInput} />
          </InputLabel>
        )}

        <InputLabel>
          <span>{copy.common.form.phone_number}</span>
          <span>{errors.phone_number}</span>
          <InputMask mask="999 999 999" maskPlaceholder={null} onBlur={validateInput}>
            <PrefixedInput name="phone_number" prefix="+48" type="tel" />
          </InputMask>
        </InputLabel>

        <InputLabel>
          <span>{copy.common.form.contact_email}</span>
          <span>{errors.contact_email}</span>
          <Input name="contact_email" onBlur={validateInput} />
        </InputLabel>

        <Select hidden defaultValue={isPublicPage ? 'WEB' : 'AGENT'} name="registration_source">
          <option value="WEB">{copy.page.registration.form.registration_source_web}</option>
          <option value="AGENT">{copy.page.registration.form.registration_source_agent}</option>
        </Select>

        {consents
          .filter(({ header }) => header !== 'IGNORE')
          .sort((a, b) => (a.name > b.name ? 1 : -1))
          .map(({ name, header, file_url }) => (
            <CheckboxLabel key={name}>
              <Checkbox name={`${name.toLowerCase()}_consent`} />
              <a className="link" href={file_url} target="_blank" rel="noreferrer">
                {header}
              </a>
            </CheckboxLabel>
          ))}

        <Button type="submit" disabled={isPublicPage && !consents.length}>
          {loading.registerUser ? <Spinner size={16} /> : copy.common.button.register}
        </Button>

        {isPublicPage && (
          <p className="dimmed">
            {copy.page.registration.form.paragraph_consents_accept}
            <a className="link" href={termsAndConditionsUrl} target="_blank" rel="noreferrer">
              {copy.page.registration.form.link_consents_accept_1}
            </a>
            {copy.page.registration.form.and}
            <a className="link" href={privacyUrl} target="_blank" rel="noreferrer">
              {copy.page.registration.form.link_consents_accept_2}
            </a>
            .
          </p>
        )}
      </Form>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;

  @media (min-width: 769px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px 0;
    padding-top: 40px;
  }
`

const Info = styled.div<{ hidden?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;

  padding: 48px 36px;

  background: var(--green);

  transition-property: transform, opacity;
  transition-duration: 0.01ms;

  ${sc('hidden', 'transform: translate(0%);')}

  @media (min-width: 769px) {
    max-width: 650px;
    margin-top: 200px;
    border-radius: 4px 0 0 4px;
    transition-duration: 0.5s;
    ${sc('hidden', 'transform: translate(50%); opacity: 0;')}
  }

  z-index: 1;

  h1 {
    font: var(--400-36);
    word-break: break-word;
  }

  p {
    font: var(--400-15);
  }

  div {
    display: flex;
    flex-wrap: wrap;
    gap: 48px;

    svg {
      width: 64px;
      height: 64px;
      stroke-width: 1;
    }
  }
`

const Form = styled.form<{ hidden?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  transition: all 0.01ms;

  padding: 24px;

  background: var(--white);
  z-index: 2;

  > * {
    width: 100%;
    max-width: 315px;
  }

  @media (min-width: 769px) {
    max-width: 650px;
    border-radius: 4px;
    transition: all 0.5s;
    ${sc('hidden', 'transform: translate(-50%); > * { opacity: 0; transition: opacity 0.5s; }')}
  }

  > h1,
  > p {
    text-align: center;
  }

  > h1 {
    font: var(--600-24);
  }

  [type='submit'] + p {
    margin-top: -8px;
    text-align: center;
  }
`
